import React, {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

type Props = {
  triggerElement: ReactNode;
  positionType?: "right" | "bottom";
  isCloseOnClick?: boolean;
  propsPosition?: boolean;
  notification?: boolean;
  children?: ReactNode | ((props: { close: () => void }) => ReactNode);
};

export const Dropdown: FC<Props> = ({
  triggerElement,
  positionType = "right",
  isCloseOnClick,
  children,
  propsPosition,
  notification,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHide, setIsHide] = useState(false);
  const [position, setPosition] = useState<CSSProperties | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target;

      if (
        menuRef.current &&
        target instanceof Node &&
        !triggerRef.current?.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight;
      const viewportGap = 8;
      const triggerGap = 4;

      if (positionType === "bottom") {
        setPosition({
          top: triggerRect.bottom + 4,
          left: triggerRect?.left,
        });
      } else {
        const preferredTop = triggerRect.top - menuHeight - triggerGap;

        setPosition({
          top: Math.max(viewportGap, preferredTop),
          left: triggerRect?.right - 110,
        });
      }

      setIsHide(false);
    }

    if (!isOpen) {
      setIsHide(true);
    }
  }, [isOpen, triggerRef, menuRef, positionType]);

  const handleTriggerClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const close = () => {
    setIsOpen(false);
  };

  const portalElement = document.getElementById("portal-root");
  if (!portalElement) {
    return null;
  }

  const menuPosition: CSSProperties = propsPosition
    ? {
        position: "fixed",
        opacity: isHide ? 0 : 1,
        top: notification ? "9rem" : "8rem",
        right: "5rem",
        fontSize: "14px",
        zIndex: 1000,
      }
    : {
        position: "fixed",
        opacity: isHide ? 0 : 1,
        ...position,
        fontSize: "14px",
        zIndex: 1000,
      };

  return (
    <>
      <div
        onClick={handleTriggerClick}
        ref={triggerRef}
        className={"w-fit cursor-pointer"}>
        {triggerElement}
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={menuPosition}
            onClick={(e) => {
              e.stopPropagation();
              if (isCloseOnClick) {
                setIsOpen(false);
              }
            }}
            ref={menuRef}>
            {typeof children === "function" ? children({ close }) : children}
          </div>,
          portalElement,
        )}
    </>
  );
};
